import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import moment from 'moment'

/* global IntersectionObserver requestAnimationFrame */

/**
 * Slot duration levels for zoom in/out.
 * Each level: [slotDuration, slotLabelInterval]
 */
const SLOT_LEVELS = [
  ['02:00:00', '02:00:00'], // Most zoomed out
  ['01:00:00', '01:00:00'],
  ['00:30:00', '01:00:00'],
  ['00:15:00', '00:30:00'] // Most zoomed in
]

export class CalendarManager {
  constructor (controller, SceneCollectionService) {
    this.controller = controller
    this.SceneCollectionService = SceneCollectionService
    this.calendar = null
    this.visibilityObserver = null
    this.lastInitTime = 0
    this.calendarUnsubscribe = null
  }

  /**
         * Component initialization lifecycle hook
         */
  init () {
    this.lastInitTime = 0 // Track calendar initialization time for debounce
    this.controller._$log.info('[ScheduleCalendar] CalendarManager initializing')

    // 1. Setup WebSocket immediately (doesn't require DOM)
    this.setupWebSocketSubscriptions()

    // 2. Wait for element visibility before drawing calendar
    this.waitForVisibilityAndInit()
  }

  /**
         * Component destruction lifecycle hook
         */
  destroy () {
    this.controller._$log.info('[ScheduleCalendar] CalendarManager destroying')

    // Clean up visibility observer if still active
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
    }

    // Clean up WebSocket subscriptions
    if (this.calendarUnsubscribe && typeof this.calendarUnsubscribe === 'function') {
      this.calendarUnsubscribe()
      this.calendarUnsubscribe = null
    }

    // Clean up calendar
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  /**
         * Calculate scroll time based on current time
         */
  calculateScrollTime () {
    const now = new Date()
    let currentHour = now.getHours()

    // Scroll to 1 hour before current time for better UI context
    currentHour = currentHour - 1

    // Default to 8 AM if too early
    if (currentHour < 7) {
      return '08:00:00'
    }

    // Format time with padding
    return `${currentHour.toString().padStart(2, '0')}:00:00`
  }

  /**
   * Cycle slot duration for zoom in/out.
   * @param {'in'|'out'} direction
   */
  _cycleSlotDuration (direction) {
    if (!this.calendar) return

    const levels = SLOT_LEVELS
    const current = this.calendar.getOption('slotDuration')

    // Normalize — FC stores it as { milliseconds: ... } or string
    const currentStr = typeof current === 'string' ? current : this._durationToString(current)

    let idx = levels.findIndex(([d]) => d === currentStr)
    if (idx === -1) idx = 1 // default fallback

    if (direction === 'in' && idx < levels.length - 1) {
      idx++
    } else if (direction === 'out' && idx > 0) {
      idx--
    } else {
      return // already at limit
    }

    // Find the scroller element and figure out which time is at the top
    const scrollerEl = this.calendar.el.querySelector('.fc-scroller-liquid-absolute') ||
      this.calendar.el.querySelector('.fc-timegrid-body')?.closest('.fc-scroller')
    let topMinutes = null

    if (scrollerEl) {
      // Find all slot-lane rows — each represents one slotDuration interval
      const slotLanes = this.calendar.el.querySelectorAll('.fc-timegrid-slot[data-time]')
      if (slotLanes.length > 0) {
        const scrollTop = scrollerEl.scrollTop
        // Find the slot whose top is closest to the current scrollTop
        for (const slot of slotLanes) {
          if (slot.offsetTop <= scrollTop) {
            const timeAttr = slot.getAttribute('data-time')
            if (timeAttr) {
              const parts = timeAttr.split(':')
              topMinutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
            }
          } else {
            break
          }
        }
      }
    }

    const [slotDuration, slotLabelInterval] = levels[idx]
    this.calendar.setOption('slotDuration', slotDuration)
    this.calendar.setOption('slotLabelInterval', slotLabelInterval)

    // After FC re-renders, manually scroll to the same time position
    if (topMinutes !== null && scrollerEl) {
      requestAnimationFrame(() => {
        // Re-query slots after re-render (new slot elements exist now)
        const newSlots = this.calendar.el.querySelectorAll('.fc-timegrid-slot[data-time]')
        let targetScrollTop = 0
        for (const slot of newSlots) {
          const timeAttr = slot.getAttribute('data-time')
          if (timeAttr) {
            const parts = timeAttr.split(':')
            const slotMinutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
            if (slotMinutes <= topMinutes) {
              targetScrollTop = slot.offsetTop
            } else {
              break
            }
          }
        }
        // Get the actual scroller after re-render (may be same element)
        const newScroller = this.calendar.el.querySelector('.fc-scroller-liquid-absolute') ||
          this.calendar.el.querySelector('.fc-timegrid-body')?.closest('.fc-scroller')
        if (newScroller) {
          newScroller.scrollTop = targetScrollTop
        }
      })
    }

    this.controller._$log.info(`[ScheduleCalendar] Zoom ${direction}: slotDuration=${slotDuration}, topMinutes=${topMinutes}`)
  }

  /**
   * Convert FullCalendar duration object to HH:MM:SS string.
   */
  _durationToString (dur) {
    const ms = dur.milliseconds || dur.ms || 0
    const totalSeconds = Math.floor(ms / 1000)
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  /**
         * Smart calendar initialization using IntersectionObserver
         * Uses component encapsulation instead of global DOM queries
         */
  waitForVisibilityAndInit () {
    // Find calendar element within component scope
    const calendarEl = this.controller._$element[0].querySelector('#schedule-calendar') ||
      (this.controller._$element.find && this.controller._$element.find('#schedule-calendar')[0]) ||
      document.getElementById('schedule-calendar') // fallback

    if (!calendarEl) {
      // Element not ready, retry
      this.controller._$timeout(() => this.waitForVisibilityAndInit(), 0)
      return
    }

    // Use IntersectionObserver to detect when calendar becomes visible
    this.visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry.isIntersecting) {
        // Element is now visible! Stop observing and initialize
        this.controller._$log.info('[ScheduleCalendar] Element visible - initializing Calendar')

        // Safely disconnect observer
        if (this.visibilityObserver) {
          this.visibilityObserver.disconnect()
          this.visibilityObserver = null
        }

        // Initialize immediately (element IS visible)
        this.initializeCalendar(calendarEl)
      }
    }, {
      root: null, // viewport
      threshold: 0.1 // trigger when 10% of calendar is visible
    })

    this.visibilityObserver.observe(calendarEl)
  }

  /**
         * Initialize the FullCalendar instance
         * @param {HTMLElement} calendarEl - The calendar container element
         */
  initializeCalendar (calendarEl) {
    if (this.calendar) {
      this.controller._$log.warn('[ScheduleCalendar] Calendar already initialized, skipping')
      return // Prevent double initialization
    }

    this.lastInitTime = Date.now() // Sæt tidsstempel når vi starter
    this.controller._$log.info('[ScheduleCalendar] Initializing calendar on visible element')

    try {
      this.calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],

        // Custom buttons MUST be defined before headerToolbar
        customButtons: {
          closeBtn: {
            text: '✕',
            hint: 'Luk kalender',
            click: () => {
              this.controller._$log.info('[ScheduleCalendar] Close button clicked')

              // Use the modal manager
              this.controller.closeCalendarModal()

              // Apply scope changes immediately
              this.controller._$scope.$apply()
            }
          },
          zoomIn: {
            text: 'Zoom +',
            hint: 'Zoom ind (finere tidsopløsning)',
            click: () => this._cycleSlotDuration('in')
          },
          zoomOut: {
            text: 'Zoom −',
            hint: 'Zoom ud (grovere tidsopløsning)',
            click: () => this._cycleSlotDuration('out')
          }
        },

        // Initial view and navigation
        initialView: 'timeGridWeek',
        headerToolbar: {
          left: 'prev,next today zoomOut,zoomIn',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek,closeBtn'
        },

        // Localization
        locale: 'da',
        firstDay: 1, // Monday

        // Suppress FC's built-in title rendering to prevent Preact from
        // injecting a text node that duplicates our custom title.
        // We compute the date range ourselves in updateCalendarTitle().
        titleFormat: () => '',

        // Time settings
        slotMinTime: '00:00:00', // Show full day from midnight
        slotMaxTime: '24:00:00', // Show until midnight next day
        slotDuration: '01:00:00', // 1-hour slots to reduce vertical scroll
        snapDuration: '00:15:00', // Drag/resize still snaps to 15-minute intervals

        // Now indicator - shows current time as a red line across calendar
        nowIndicator: true,

        // Remove all-day area
        allDaySlot: false,

        // Scroll to current time (or reasonable fallback)
        scrollTime: this.calculateScrollTime(), // Enable editing and selection
        editable: true,
        selectable: true,
        selectMirror: true,
        eventResizableFromStart: true,
        dragRevertDuration: 300,
        eventDurationEditable: true,

        // Visual feedback during drag
        eventMouseEnter: (info) => {
          info.el.style.cursor = 'move'
          info.el.title = 'Drag to move, resize handles to change duration'
        },

        // Event handling - delegate to controller methods which might be mixins or other managers
        eventClick: (info) => this.handleEventClick(info),
        dateClick: (info) => this.handleDateClick(info),
        select: (info) => this.handleTimeSelection(info),
        eventDrop: (info) => this.handleEventDrop(info),
        eventResize: (info) => this.handleEventResize(info),
        eventDidMount: (info) => this.styleEventBasedOnPriority(info),

        // Custom content rendering with adaptive layout based on duration
        eventContent: (arg) => {
          const duration = moment(arg.event.end).diff(moment(arg.event.start), 'minutes')

          // Very short events - minimal styling
          if (duration <= 15) {
            return {
              html: `
                <div style="display: flex; flex-direction: row; align-items: center; gap: 3px; 
                           padding: 0 !important; margin: 0 !important; line-height: 1; font-size: 0.8em; 
                           position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                           background: inherit; border-radius: inherit;">
                  <span style="white-space: nowrap; font-weight: bold; margin-left: 2px;">${arg.timeText}</span>
                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${arg.event.title}</span>
                </div>
              `
            }
          }

          // Short events - horizontal layout
          if (duration <= 30) {
            return {
              html: `
                <div class="fc-event-main-frame" style="flex-direction: row; align-items: center; gap: 5px; padding: 0 4px; line-height: 1;">
                  <div class="fc-event-time" style="font-size: 0.85em; white-space: nowrap; font-weight: bold;">${arg.timeText}</div>
                  <div class="fc-event-title" style="font-size: 0.85em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${arg.event.title}</div>
                </div>
              `
            }
          }

          // Longer events - vertical layout
          return {
            html: `
              <div class="fc-event-main-frame">
                <div class="fc-event-time" style="font-weight: bold;">${arg.timeText}</div>
                <div class="fc-event-title">${arg.event.title}</div>
              </div>
            `
          }
        },

        // Update title when view changes
        datesSet: (info) => {
          // Use a longer timeout to ensure FC's Preact rendering is fully complete
          // before we modify the title DOM. FC may do multiple render passes.
          this.controller._$timeout(() => {
            this.updateCalendarTitle()
          }, 50)
        },

        // Optimize for modal display
        height: '96vh', // Adjusted to match modal height and avoid scrollbars
        windowResizeDelay: 100, // Optimization for window resizing
        // aspectRatio: 1.6,

        // Load real events
        eventSources: [{
          events: (fetchInfo, successCallback, failureCallback) => {
            this.loadCalendarEvents(fetchInfo.start, fetchInfo.end)
              .then(events => successCallback(events))
              .catch(error => {
                this.controller._$log.error('[ScheduleCalendar] Failed to load events:', error)
                failureCallback(error)
              })
          }
        }]
      })

      this.calendar.render()
      this.controller._$log.info('[ScheduleCalendar] Calendar rendered successfully')

      // Title update handled automatically by datesSet hook - no manual call needed
    } catch (error) {
      this.controller._$log.error('[ScheduleCalendar] Failed to initialize calendar:', error)
    }
  }

  /**
        * Set up WebSocket subscriptions for real-time updates
        */
  setupWebSocketSubscriptions () {
    this.controller._$log.info(`[ScheduleCalendar] Setting up WebSocket subscriptions for scene ${this.controller.sceneId.id}`)

    // Subscribe to calendar updates
    this.calendarUnsubscribe = this.SceneCollectionService.subscribeToCalendarUpdates(
      this.controller.sceneId.id,
      (error, update) => {
        if (!error && this.calendar) {
          this.handleRealTimeUpdate(update)
        }
      }
    )
  }

  /**
        * Handle real-time WebSocket updates
        */
  handleRealTimeUpdate (update) {
    this.controller._$log.info('[ScheduleCalendar] Received real-time update:', update.type)

    // Refresh calendar events when intervals or scene collections change
    if (update.type === 'interval' || update.type === 'sceneCollection') {
      this.controller._$timeout(() => {
        this.calendar.refetchEvents()
      }, 100) // Small delay to ensure backend changes are committed
    }
  }

  /**
         * Force reinitialize calendar (destroy and recreate)
         */
  forceInitialize () {
    this.controller._$log.info('[ScheduleCalendar] Force reinitializing calendar')

    // Clean up existing calendar
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }

    // Clean up existing visibility observer
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
    }

    // Reset init time to prevent stale debounce
    this.lastInitTime = 0

    // Start fresh initialization immediately
    this.controller._$timeout(() => {
      this.waitForVisibilityAndInit()
    }, 0)
  }

  /**
        * Load calendar events for a date range
        */
  loadCalendarEvents (start, end) {
    if (!this.controller.sceneId || !this.controller.sceneId.id) {
      this.controller._$log.warn('[ScheduleCalendar] No scene ID available')
      return Promise.resolve([])
    }

    const startDate = moment(start).format('YYYY-MM-DD HH:mm:ss')
    const endDate = moment(end).format('YYYY-MM-DD HH:mm:ss')

    this.controller._$log.info(`[ScheduleCalendar] Loading events for scene ${this.controller.sceneId.id} from ${startDate} to ${endDate}`)

    return this.SceneCollectionService.getCalendarEvents(this.controller.sceneId.id, startDate, endDate)
      .then(sceneCollections => {
        const events = this.SceneCollectionService.formatIntervalsForCalendar(sceneCollections)
        return events
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Error loading calendar events:', error)
        return []
      })
  }

  /**
         * Update calendar title with scene name and date range
         */
  updateCalendarTitle () {
    if (!this.calendar) {
      return
    }

    const sceneName = (this.controller.sceneId && this.controller.sceneId.name) ? this.controller.sceneId.name : 'Schedule'
    const sceneColor = (this.controller.sceneId && this.controller.sceneId.color) ? this.controller.sceneId.color : null

    // Build date range string ourselves using moment, independent of FC's
    // titleFormat/viewTitle (which Preact may re-render and duplicate).
    const currentView = this.calendar.view
    const rangeStart = moment(currentView.activeStart)
    const rangeEnd = moment(currentView.activeEnd).subtract(1, 'day')
    let viewTitle
    if (rangeStart.isSame(rangeEnd, 'day')) {
      viewTitle = rangeStart.format('D. MMM YYYY')
    } else if (rangeStart.isSame(rangeEnd, 'month')) {
      viewTitle = `${rangeStart.format('D.')} – ${rangeEnd.format('D. MMM YYYY')}`
    } else if (rangeStart.isSame(rangeEnd, 'year')) {
      viewTitle = `${rangeStart.format('D. MMM')} – ${rangeEnd.format('D. MMM YYYY')}`
    } else {
      viewTitle = `${rangeStart.format('D. MMM YYYY')} – ${rangeEnd.format('D. MMM YYYY')}`
    }

    // Replace the entire h2 title element's content to avoid Preact ghost text nodes.
    // FC's Preact renderer controls the h2 textContent; setting innerHTML alone
    // can leave orphan text nodes that show the date range twice after re-renders.
    const headerEl = this.calendar.el.querySelector('.fc-toolbar-title')
    if (headerEl) {
      const colorStyle = sceneColor ? `color: ${sceneColor};` : ''
      // textContent = '' wipes Preact's text node before we inject our HTML
      headerEl.textContent = ''
      headerEl.innerHTML = `
        <div style="text-align: center;">
          <div style="font-size: 1.2em; font-weight: 600; margin-bottom: 4px; ${colorStyle}">${sceneName}</div>
          <div style="font-size: 0.9em; color: #666;">${viewTitle}</div>
        </div>
      `
    }

    // Apply scene color to toolbar bottom border (separator between header and calendar)
    const toolbarEl = this.calendar.el.querySelector('.fc-toolbar')
    if (toolbarEl) {
      toolbarEl.style.borderBottomColor = sceneColor || ''
    }

    this.controller._$log.info(`[ScheduleCalendar] Updated calendar title to: ${sceneName} with date range: ${viewTitle}`)
  }

  // --- Event Handlers (Migrated from EventHandlersMixin) ---

  /**
         * Handle event click for editing
         * @param {Object} info - FullCalendar event click info
         */
  handleEventClick (info) {
    this.controller._$log.info('[ScheduleCalendar] Event clicked:', info.event.title)
    this.controller.openEventEditModal(info.event)
  }

  /**
         * Handle date click (single click on calendar)
         * @param {Object} info - FullCalendar date click info
         */
  handleDateClick (info) {
    this.controller._$log.info('[ScheduleCalendar] Date clicked:', info.dateStr)
    // Single click - show quick info or do nothing for now
  }

  /**
         * Handle time range selection (drag to select time)
         * @param {Object} info - FullCalendar selection info with start/end times
         */
  handleTimeSelection (info) {
    this.controller._$log.info('[ScheduleCalendar] Time range selected:', {
      start: info.startStr,
      end: info.endStr,
      duration: moment(info.end).diff(moment(info.start), 'minutes') + ' minutes'
    })

    // Open create interval modal with selected time range
    this.controller.openCreateIntervalModal(info.start, info.end)

    // Clear the selection
    this.calendar.unselect()
  }

  /**
         * Handle event drop (drag & drop move)
         * @param {Object} info - FullCalendar drop info
         */
  handleEventDrop (info) {
    const event = info.event
    const extendedProps = event.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      this.controller._$log.warn('[ScheduleCalendar] No interval data for dropped event')
      info.revert() // Revert the drop
      return
    }

    const newStart = moment(event.start).format() // ISO 8601 with timezone
    const newEnd = moment(event.end).format() // ISO 8601 with timezone

    this.controller._$log.info('[ScheduleCalendar] Event dropped:', {
      intervalId: extendedProps.intervalId,
      oldStart: info.oldEvent.start,
      newStart: event.start,
      oldEnd: info.oldEvent.end,
      newEnd: event.end,
      priority: extendedProps.priority || 1
    })

    // Check for overlaps before updating
    const overlappingEvents = this.controller.conflictManager.findOverlappingEvents(event)
    if (overlappingEvents.length > 0) {
      this.controller._$log.info('[ScheduleCalendar] Overlap detected during drop, showing conflict dialog')

      this.controller.conflictManager.showOverlapDialog(event, overlappingEvents, info)
    } else {
      // No overlaps, proceed with update
      this.updateIntervalTime(extendedProps.intervalId, newStart, newEnd, info)
        .catch(error => {
          this.controller._$log.error('[ScheduleCalendar] Failed to update interval after drop:', error)
          info.revert() // Revert on error
          window.alert('Failed to move interval. Please try again.')
        })
    }
  }

  /**
         * Handle event resize (duration change)
         * @param {Object} info - FullCalendar resize info
         */
  handleEventResize (info) {
    const event = info.event
    const extendedProps = event.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      this.controller._$log.warn('[ScheduleCalendar] No interval data for resized event')
      info.revert() // Revert the resize
      return
    }

    const newStart = moment(event.start).format() // ISO 8601 with timezone
    const newEnd = moment(event.end).format() // ISO 8601 with timezone

    this.controller._$log.info('[ScheduleCalendar] Event resized:', {
      intervalId: extendedProps.intervalId,
      oldStart: info.oldEvent.start,
      newStart: event.start,
      oldEnd: info.oldEvent.end,
      newEnd: event.end,
      priority: extendedProps.priority || 1
    })

    // Check for overlaps after resize
    const overlappingEvents = this.controller.conflictManager.findOverlappingEvents(event)
    if (overlappingEvents.length > 0) {
      this.controller._$log.info('[ScheduleCalendar] Overlap detected during resize, showing conflict dialog')

      this.controller.conflictManager.showOverlapDialog(event, overlappingEvents, info)
    } else {
      // No overlaps, proceed with update
      this.updateIntervalTime(extendedProps.intervalId, newStart, newEnd, info)
        .catch(error => {
          this.controller._$log.error('[ScheduleCalendar] Failed to update interval after resize:', error)
          info.revert() // Revert on error
          window.alert('Failed to resize interval. Please try again.')
        })
    }
  }

  /**
         * Update interval time via API
         * @param {number} intervalId - The interval ID to update
         * @param {string} newStart - New start time (YYYY-MM-DD HH:mm:ss)
         * @param {string} newEnd - New end time (YYYY-MM-DD HH:mm:ss)
         * @param {Object} info - FullCalendar info object for potential revert
         * @returns {Promise} API call promise
         */
  updateIntervalTime (intervalId, newStart, newEnd, info, sceneCollectionId = null, priority = null) {
    // Handle both moment objects and string inputs - always use ISO 8601 with timezone
    const startString = typeof newStart === 'string' ? newStart : newStart.format()
    const endString = typeof newEnd === 'string' ? newEnd : newEnd.format()

    // Get missing data from calendar event if not provided
    let finalSceneCollectionId = sceneCollectionId
    let finalPriority = priority

    if (!finalSceneCollectionId || finalPriority === null) {
      // Try to find the event by intervalId
      let calendarEvent = null
      if (this.calendar) {
        // Try multiple ways to find the event
        calendarEvent = this.calendar.getEventById(intervalId) ||
          this.calendar.getEventById(`interval_${intervalId}`)
      }

      this.controller._$log.info('[ScheduleCalendar] Looking for event data:', {
        intervalId,
        foundEvent: !!calendarEvent,
        eventId: calendarEvent ? calendarEvent.id : null,
        extendedProps: calendarEvent ? calendarEvent.extendedProps : null
      })

      if (calendarEvent && calendarEvent.extendedProps) {
        finalSceneCollectionId = finalSceneCollectionId || calendarEvent.extendedProps.sceneCollectionId
        finalPriority = finalPriority !== null ? finalPriority : calendarEvent.extendedProps.priority
      } else {
        this.controller._$log.warn('[ScheduleCalendar] Could not find calendar event for intervalId:', intervalId)
        // Try to get from all calendar events
        const allEvents = this.calendar ? this.calendar.getEvents() : []
        this.controller._$log.info('[ScheduleCalendar] All calendar events:', allEvents.map(e => ({ id: e.id, extendedProps: e.extendedProps })))
      }
    }

    const intervalData = {
      id: intervalId,
      sceneCollectionId: finalSceneCollectionId,
      interval_start: startString,
      interval_end: endString,
      priority: finalPriority
    }

    // Validate all required fields are present
    if (!intervalData.sceneCollectionId) {
      throw new Error(`Missing sceneCollectionId for interval ${intervalId}`)
    }
    if (intervalData.priority === null || intervalData.priority === undefined) {
      throw new Error(`Missing priority for interval ${intervalId}`)
    }

    this.controller._$log.info('[ScheduleCalendar] Updating interval time:', {
      intervalId,
      intervalData,
      providedParams: { sceneCollectionId, priority },
      calendarEventExists: !!this.calendar.getEventById(intervalId),
      finalValues: { finalSceneCollectionId, finalPriority }
    })

    return this.SceneCollectionService.editInterval(intervalData)
      .then(updatedInterval => {
        this.controller._$log.info('[ScheduleCalendar] Interval time updated successfully:', updatedInterval)

        // Calendar will update via WebSocket - no need to manually refetch
        return updatedInterval
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Failed to update interval time:', error)
        if (info && info.revert) {
          info.revert() // Revert the visual change
        }
        throw error // Re-throw for caller to handle
      })
  }

  // --- Styling (Migrated from EventStylingMixin) ---

  /**
         * Style events based on priority conflicts and winners/losers
         * @param {Object} info - FullCalendar eventDidMount info
         */
  styleEventBasedOnPriority (info) {
    const event = info.event
    const eventEl = info.el
    const extendedProps = event.extendedProps

    if (!extendedProps || !extendedProps.intervalId) {
      return // Skip non-interval events
    }

    // Check for overlapping events
    const overlappingEvents = this.controller.conflictManager.findOverlappingEvents(event)

    if (overlappingEvents.length > 0) {
      // This event has overlaps, check if it's winner or loser
      const allConflictingEvents = [event, ...overlappingEvents]
      const highestPriority = Math.max(...allConflictingEvents.map(e => e.extendedProps.priority || 0))
      const lowestPriority = Math.min(...allConflictingEvents.map(e => e.extendedProps.priority || 0))

      const currentPriority = extendedProps.priority || 0

      if (currentPriority === highestPriority && currentPriority > lowestPriority) {
        // Winner - green border and background tint
        eventEl.style.borderLeft = '4px solid #28a745'
        eventEl.style.backgroundColor = '#d4edda'
        eventEl.style.borderColor = '#28a745'
        eventEl.classList.add('conflict-winner')
      } else if (currentPriority === lowestPriority && currentPriority < highestPriority) {
        // Loser - yellow border and background tint (still plays when higher-priority intervals aren't active)
        eventEl.style.borderLeft = '4px solid #ffc107'
        eventEl.style.backgroundColor = '#fff3cd'
        eventEl.style.borderColor = '#ffc107'
        eventEl.classList.add('conflict-loser')
      } else {
        // Middle priority - yellow border
        eventEl.style.borderLeft = '4px solid #ffc107'
        eventEl.style.backgroundColor = '#fff3cd'
        eventEl.style.borderColor = '#ffc107'
        eventEl.classList.add('conflict-middle')
      }

      // Add tooltip with conflict info
      eventEl.title = `Priority: ${currentPriority} | Conflicts with ${overlappingEvents.length} other interval${overlappingEvents.length !== 1 ? 's' : ''}`
    }
  }

  /**
         * Refresh styling for all events after conflicts are resolved
         */
  refreshAllEventStyles () {
    if (!this.calendar) return

    const allEvents = this.calendar.getEvents()
    allEvents.forEach(event => {
      // Remove existing conflict classes
      const eventEl = event.el
      if (eventEl) {
        eventEl.classList.remove('conflict-winner', 'conflict-loser', 'conflict-middle')
        // Reset inline styles
        eventEl.style.borderLeft = ''
        eventEl.style.backgroundColor = ''
        eventEl.style.borderColor = ''
        eventEl.title = event.title || ''

        // Re-apply styling based on current conflicts
        this.styleEventBasedOnPriority({ event, el: eventEl })
      }
    })

    this.controller._$log.info('[ScheduleCalendar] Refreshed styling for all events')
  }
}
